import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import clsx from 'clsx'
import {
  marketMapState,
  marketSymbolState,
  marketIsReadyValue
} from '@/recoil/Market'
import { userConfigState, layoutState } from '@/recoil/UserConfig'
import { showSettlePnlPopupState } from '@/recoil/Position'
import { showAdjustLeveragePopupState } from '@/recoil/AccountInfo'
import GridLayout from '@/components/GridLayout'
import MarketChart from '@/components/trade/MarketChart'
import OrderBook from '@/components/trade/OrderBook'
import OrderFormPanel from '@/components/trade/OrderFormPanel'
import RecentTrades from '@/components/trade/RecentTrades'
import Orders from '@/components/trade/Orders'
import CancelAllConfirmationPopup from '@/components/CancelAllConfirmationPopup'
import SettlePnlPopup from '@/components/trade/SettlePnlPopup'
import AdjustLeveragePopup from '@/components/trade/AdjustLeveragePopup'
import ClosePositionPopup from '@/components/trade/ClosePositionPopup'
import { getDefaultLayoutConfig, saveLayout } from '@/utils/layout'
import { toggleLocalStorageWatcher } from '@/utils/localStorage'
import {
  LOCAL_STORAGE_LAYOUT_CONFIG_KEY,
  LAYOUT_BREAKPOINTS,
  LAYOUT_COLS
} from '@/constants/layout'
import { DEFAULT_ROUTE_MAP } from '@/constants/config'
import './Trade.css'

export default function Trade() {
  const { marketSymbol = '' } = useParams()
  const navigate = useNavigate()
  const [marketMap] = useRecoilState(marketMapState)
  const [userConfig] = useRecoilState(userConfigState)
  const [layout, setLayout] = useRecoilState(layoutState)
  const [showSettlePnlPopup, setShowSettlePnlPopup] = useRecoilState(
    showSettlePnlPopupState
  )
  const [showAdjustLeveragePopup, setShowAdjustLeveragePopup] = useRecoilState(
    showAdjustLeveragePopupState
  )
  const marketIsReady = useRecoilValue(marketIsReadyValue)
  const setMarketSymbol = useSetRecoilState(marketSymbolState)
  const [active, setActive] = useState(false)
  const [marketType] = marketSymbol.split('_')
  const layoutKey = marketType === 'PERP' ? 'futures' : 'spot'

  const onLayoutChange = useCallback(
    (_, layout) => {
      if (layoutKey === null) {
        return
      }

      setLayout(oldLayout => {
        const newLayout = JSON.parse(JSON.stringify(oldLayout))
        newLayout[layoutKey] = layout

        saveLayout(newLayout)
        return newLayout
      })
    },
    [layoutKey, setLayout]
  )

  useEffect(() => setActive(true), [])

  useEffect(() => {
    const onStorageChange = ({ key, newValue }) => {
      if (key === LOCAL_STORAGE_LAYOUT_CONFIG_KEY) {
        try {
          const layoutConfig = JSON.parse(newValue) || getDefaultLayoutConfig()
          setLayout(layoutConfig.layouts)
        } catch (e) {}
      }
    }
    toggleLocalStorageWatcher(onStorageChange)
    return () => toggleLocalStorageWatcher(onStorageChange, false)
  }, [setLayout])

  useEffect(() => {
    if (!marketIsReady) {
      return
    }

    if (!marketMap[marketSymbol]) {
      const wrapUsdcMarketSymbol = marketSymbol.replace('_USDC', '_USDC.e')
      const path = marketMap[wrapUsdcMarketSymbol]
        ? `/trade/${wrapUsdcMarketSymbol}`
        : DEFAULT_ROUTE_MAP.trade
      return navigate(path, { replace: true })
    }
    setMarketSymbol(marketSymbol)
  }, [navigate, marketIsReady, marketSymbol, marketMap, setMarketSymbol])

  return (
    <div className={clsx('trade-container', { active })}>
      <GridLayout
        breakpoints={LAYOUT_BREAKPOINTS}
        cols={LAYOUT_COLS}
        layouts={layout[layoutKey]}
        rowHeight={20}
        draggableHandle=".card-draggable-area"
        isDraggable={!userConfig.lockLayout}
        isResizable={!userConfig.lockLayout}
        onLayoutChange={onLayoutChange}
      >
        <MarketChart key="MarketChart" />
        <OrderBook key="OrderBook" />
        <RecentTrades key="RecentTrades" />
        <Orders key="Orders" />
        <OrderFormPanel key="OrderForm" />
      </GridLayout>
      <CancelAllConfirmationPopup symbol={marketSymbol} />
      <SettlePnlPopup
        show={showSettlePnlPopup}
        hidePopupCallback={() => setShowSettlePnlPopup(false)}
      />
      <AdjustLeveragePopup
        show={showAdjustLeveragePopup}
        hidePopupCallback={() => setShowAdjustLeveragePopup(false)}
      />
      <ClosePositionPopup />
    </div>
  )
}
